import React from 'react';
import {
  Component,
  HBox,
  Position,
  PuzzleButton, 
  PuzzleGroupedCheckboxDropdownList,
  PuzzleToggle
} from '../../index';
import Dashboard from './Dashboard';
import DashboardGrid from './DashboardGrid';
import _ from 'lodash';
import {
  CONFIGURATION_BY_TERMBASE_IDS,
  DASHBOARD_COLUMN_CONFIG,
  DASHBOARD_GRID_CONFIG,
  TERM_DASHBOARD_PROPS,
  TERM_DASHBOARD_STATE,
  TERM_ENTRY_ITEM,
  TERMBASE,
  TERMBASE_LANGUAGE
} from './Types';
import { GROUPED_CHECKBOXES_CONFIG } from '../UI/checkbox/Types';
import PuzzleCheckboxMultiselect from '../UI/checkbox/PuzzleCheckboxMultiselect';
import { CELL_TAG_PROPS } from '../grid/cell_renderer/Types';

const TERM_STATUS_ICON_MAP = {
  'Approved': 'tp-term-approved tp-color-green-500',
  'On hold': 'tp-term-on-hold tp-color-orange-400',
  'Blocklisted': 'tp-term-blocklisted tp-color-magenta-500',
  'Suggested': 'tp-term-suggested tp-color-neutral-dark-400',
  'Pending approval': 'tp-term-pending tp-color-blue-500'
};

const TERM_COLUMN_CUSTOM_CONFIGS = {
  'Term status': { 
    cellConfig: {
      align: 'center' as const,
      getIcon: value => TERM_STATUS_ICON_MAP[value],
      getTooltip: (value: string) => value
    },
    cellType: 'icon'
  }
};

const DISABLED_ATTRIBUTES = ['Term status'];
const PRESELECTED_ATTRIBUTES = ['Term status'];

const PREDEFINED_ATTRIBUTE_OPTIONS = {
  terms: 'terms',
  mandatory: 'mandatory',
  all: 'all'
};

const TERM_ENTRY_PREDEFINED_COLUMNS = ['modifiedOn', 'modifiedBy', 'createdOn', 'createdBy'];
const TERM_PREDEFINED_COLUMNS = ['modifiedOn', 'modifiedBy'];

const PREDEFINED_COLUMN_CONFIGS = {
  modifiedOn: {
    cellConfig: {
      format: 'MMM. D, YYYY h:mm:ss'
    },
    cellType: 'date',
    width: 200
  },
  modifiedBy: {
    cellType: 'text',
    width: 140
  },
  createdOn: {
    cellConfig: {
      format: 'MMM. D, YYYY h:mm:ss'
    },
    cellType: 'date',
    width: 200
  },
  createdBy: {
    cellType: 'text',
    width: 140
  }
};

class TermDashboard extends Component<TERM_DASHBOARD_PROPS, TERM_DASHBOARD_STATE> {
  state = {
    dataItem: {} as TERMBASE | TERM_ENTRY_ITEM,
    activeTabUniqueName: 'home',
    isRowSelected: false,
    showSynonyms: false,
    configurationsByTermbaseIds: {} as CONFIGURATION_BY_TERMBASE_IDS,
    expandAll: false
  }

  /**
   * Getter for selected languages of passed termbase ticket
   * @param ticket
   */
  getSelectedTermLanguages = ticket => this.state.configurationsByTermbaseIds[ticket].languagesMap.languages;

  /**
   * On tabpanel active tab selection change
   * @param activeTabUniqueName
   */
  private onSelectActiveTab = (activeTabUniqueName: string): void => {
    this.setState({
      activeTabUniqueName: activeTabUniqueName
    });
  }

  getTermAttributes = (ticket: string) => this.state.configurationsByTermbaseIds[ticket].termbaseAttributes
    .filter(attr => attr.termAttribute);
  getTermMandatoryAttributes = (ticket: string) => this.getTermAttributes(ticket).filter(attr => attr.mandatory);
  getTermNonMandatoryAttributes = (ticket: string) => this.getTermAttributes(ticket).filter(attr => !attr.mandatory);

  getTermEntryAttributes = (ticket: string) => this.state.configurationsByTermbaseIds[ticket].termbaseAttributes
    .filter(attr => !attr.termAttribute)
  getTermEntryMandatoryAttributes = (ticket: string) => this.getTermEntryAttributes(ticket).filter(attr => attr.mandatory);
  getTermEntryNonMandatoryAttributes = (ticket: string) => this.getTermEntryAttributes(ticket).filter(attr => !attr.mandatory);

  /**
   * Getter for term header attributes configuration
   * @param ticket
   */
  getHeaderTermAttributesConfig = (ticket, showDynamicColumnsOnly = false) => {
    let attributesConfig: GROUPED_CHECKBOXES_CONFIG = showDynamicColumnsOnly ? [] : [{
      isStandalone: true,
      items: [{
        label: this.translate('columns.modifiedOn'),
        value: 'modifiedOn'
      }]
    }, {
      isStandalone: true,
      items: [{
        label: this.translate('columns.modifiedBy'),
        value: 'modifiedBy'
      }]
    }];

    const termMandatoryAttributeItems = this.getTermMandatoryAttributes(ticket).map(attribute => ({
      value: attribute.name,
      label: this.translate(`attributes.${attribute.name}`),
      disabled: DISABLED_ATTRIBUTES.includes(attribute.name)
    }));

    const termNonMandatoryAttributeItems = this.getTermNonMandatoryAttributes(ticket).map(attribute => ({
      value: attribute.name,
      label: this.translate(`attributes.${attribute.name}`)
    }));

    attributesConfig.push({
      groupName: this.translate('mandatoryTermAttributes'),
      items: termMandatoryAttributeItems
    });
    if (termMandatoryAttributeItems.length === 0) {
      attributesConfig.push({
        isSeparator: true
      });
    }

    attributesConfig.push({
      groupName: this.translate('nonMandatoryTermAttributes'),
      items: termNonMandatoryAttributeItems
    });
    if (termNonMandatoryAttributeItems.length === 0) {
      attributesConfig.push({
        isSeparator: true
      });
    }

    return attributesConfig;
  }
  /**
   * Getter for header attributes configuration
   * @param ticket
   */
  getHeaderTermEntryAttributesConfig = (ticket) => {
    const termEntryMandatoryAttributes = this.getTermEntryMandatoryAttributes(ticket).map(attribute => ({
      label: this.translate(`attributes.${attribute.name}`),
      value: attribute.name
    }));
    const termEntryNonMandatoryAttributes = this.getTermEntryNonMandatoryAttributes(ticket).map(attribute => ({
      label: this.translate(`attributes.${attribute.name}`),
      value: attribute.name
    }));

    let attributesConfig: GROUPED_CHECKBOXES_CONFIG = TERM_ENTRY_PREDEFINED_COLUMNS.map(columnName => ({
      isStandalone: true,
      items: [{
        label: this.translate(`columns.${columnName}`),
        value: columnName
      }]
    }));

    attributesConfig.push({
      groupName: this.translate('mandatoryEntryAttributes'),
      items: termEntryMandatoryAttributes
    });
    if (termEntryMandatoryAttributes.length === 0) {
      attributesConfig.push({ isSeparator: true });
    }

    attributesConfig.push({
      groupName: this.translate('nonMandatoryEntryAttributes'),
      items: termEntryNonMandatoryAttributes
    });
    if (termEntryNonMandatoryAttributes.length === 0) {
      attributesConfig.push({ isSeparator: true });
    }

    attributesConfig = attributesConfig.concat(this.getHeaderTermAttributesConfig(ticket, true));
    return attributesConfig;
  }

  /**
   * Renderer for language custom header
   * @param ticket
   * @param language
   */
  renderLanguageHeaderCell = (ticket, language: TERMBASE_LANGUAGE) => {
    let anchorIcon;
    if (this.state.configurationsByTermbaseIds[ticket].languagesMap.anchorLanguage === language.locale) {
      anchorIcon = (<div className={'tp-icon tp-anchor'} />);
    }
    const headerContent = (
      <span className={'k-column-title'}>
        {anchorIcon}
        {language.value}
        <span className={'tp-icon tp-arrow-chevron-down'} />
      </span>
    );

    return (
      <div className={'k-link tp-header-language'}>
        <span />
        <PuzzleGroupedCheckboxDropdownList
          component={headerContent}
          onChange={(data) => {
            this.onColumnReconfigureVisibility(data, ticket, language.locale);
          }}
          selected={this.state.configurationsByTermbaseIds[ticket].columnVisibilityByLanguages[language.locale]}
          config={this.getHeaderTermAttributesConfig(ticket)}
        />
        {this.getAttributesControl(ticket, language.locale, false)}
      </div>
    );
  }

  /**
   * Getter for column configuration by language
   * @param termId
   * @param language
   */
  getColumnConfigByLanguage = (ticket, language, orderIndex): DASHBOARD_COLUMN_CONFIG => {
    const columnsVisibility = this.state.configurationsByTermbaseIds[ticket].columnVisibilityByLanguages[language.locale];
    const termbaseAttributes = this.getTermbaseAttributes(ticket).filter(attribute => attribute.termAttribute);
    return ({
      header: language.value,
      headerRenderer: () => this.renderLanguageHeaderCell(ticket, language),
      dataIndex: language.locale,
      cellType: 'text',
      orderIndex,
      width: 330,
      minResizableWidth: 330,
      isGrouped: true,
      subColumns: [{
        header: this.translate('columns.term'),
        dataIndex: `${language.locale}-termValue`,
        cellType: 'ellipsisExpandText',
        width: 330,
        minResizableWidth: 330
      },
      ...termbaseAttributes.map((termbaseAttribute) => {
        const customColumnConfig = termbaseAttribute.type === 'COMBO' ? {
          cellType: 'tag',
          cellConfig: {
            withLastTagDifferentColor: false,
            withArrow: false
          }
        } as Partial<CELL_TAG_PROPS> : {
          cellType: 'text'
        };

        const customConfigOverride = TERM_COLUMN_CUSTOM_CONFIGS[termbaseAttribute.name];
        customConfigOverride && _.merge(customColumnConfig, customConfigOverride);

        return ({
          header: this.translate(`attributes.${termbaseAttribute.name}`),
          dataIndex: `${language.locale}-${termbaseAttribute.name}`,
          hidden: !columnsVisibility[termbaseAttribute.name],
          width: 160,
          ...customColumnConfig
        });
      }), {
        header: this.translate('columns.modifiedOn'),
        dataIndex: `${language.locale}-modifiedOn`,
        cellType: 'text',
        hidden: !columnsVisibility.modifiedOn,
        width: 140
      }, {
        header: this.translate('columns.modifiedBy'),
        dataIndex: `${language.locale}-modifiedBy`,
        cellType: 'text',
        hidden: !columnsVisibility.modifiedBy,
        width: 140
      }]
    });
  }

  getTermEntryAttributeColumns = (ticket) => {
    const columns = [];
    const { termbaseAttributes, columnVisibilityByEntry } = this.state.configurationsByTermbaseIds[ticket];
    termbaseAttributes.forEach((attribute) => {
      if (!attribute.termAttribute) {
        columns.push({
          header: this.translate(`attributes.${attribute.name}`),
          dataIndex: attribute.name,
          cellType: 'text',
          orderIndex: 1,
          hidden: !columnVisibilityByEntry[attribute.name],
          width: 200
        });
      }
    });
    return columns;
  }

  /**
   * Getter for terms grid column configuration
   * @param ticket
   */
  getTermColumns = (ticket: string): DASHBOARD_COLUMN_CONFIG[] => {
    const { columnVisibilityByEntry, visibleLanguages } = this.state.configurationsByTermbaseIds[ticket];
    let termColumns: DASHBOARD_COLUMN_CONFIG[] = [{
      header: 'Select',
      dataIndex: 'checkboxAll',
      cellType: 'checkbox',
      headerCellType: 'checkbox',
      preventSubitemRender: true,
      orderIndex: 0,
      width: 50
    }];
    termColumns = termColumns.concat(this.getTermEntryAttributeColumns(ticket));
    visibleLanguages.map((language, index) =>
      this.getColumnConfigByLanguage(ticket, language, 2 + index)).forEach((item: DASHBOARD_COLUMN_CONFIG) => {
      termColumns.push(item);
    });
    termColumns = termColumns.concat(TERM_ENTRY_PREDEFINED_COLUMNS.map(columnName => ({
      header: this.translate(`columns.${columnName}`),
      dataIndex: columnName,
      hidden: !columnVisibilityByEntry[columnName],
      ...(PREDEFINED_COLUMN_CONFIGS[columnName] || {})
    })));
    return termColumns;
  }

  /**
   * Getter for information of preselected groupedCheckbox items
   * @param ticket
   */
  getGroupedCheckboxGlobalSelection = (ticket) => {
    const {
      columnVisibilityByLanguages,
      columnVisibilityByEntry
    } = this.state.configurationsByTermbaseIds[ticket];
    const checkedItems = { ...columnVisibilityByEntry };
    Object.values(columnVisibilityByLanguages).forEach((configuration) => {
      Object.keys(configuration).forEach((attr) => {
        checkedItems[attr] = _.isBoolean(checkedItems[attr]) ? checkedItems[attr] && configuration[attr] : configuration[attr];
      });
    });
    return checkedItems;
  }

  /**
   * Renderer for dashboard global columns control actions
   * @param ticket
   */
  getDashboardTermActions = (ticket: string) => [
    <HBox gap={2} alignItemsCenter>
      <PuzzleToggle
        cssClass="k-justify-content-start"
        setActiveTab={() => {
          this.setState(prevState => ({
            expandAll: !prevState.expandAll
          }), () => {
            this.dispatch('expandAllTreeGrid', Position.end);
          });
        }}
        checked={this.state.expandAll}
        switcherOptions={[
          {
            locale: '',
            name: 'collapsed'
          },
          {
            locale: this.translate('expandCollapseAllToggleButton.expanded'),
            name: 'expanded'
          }
        ]}
      />
      {this.getAttributesControl(ticket, undefined, true)}
      <PuzzleGroupedCheckboxDropdownList
        onChange={(data) => {
          this.onColumnReconfigureVisibility(data, ticket);
        }}
        config={this.getHeaderTermEntryAttributesConfig(ticket)}
        selected={this.getGroupedCheckboxGlobalSelection(ticket)}
        component={(
          <PuzzleButton 
            size={'medium'} 
            themeColor={'primary'} 
            fillMode={'outline'}
          >
            <HBox alignItemsCenter>
              {this.translate('customColumns')}
              <span className={'tp-icon tp-arrow-chevron-down'} />
            </HBox>
          </PuzzleButton>
        )}
      />
    </HBox>
  ]

  /**
   * Setter for active grid column configurations
   * @param ticket
   * @param predefinedAttributeType
   * @param locale
   */
  setGridColumnConfig = (ticket, predefinedAttributeType, locale) => {
    const lastSelectedColumnConfig = locale ? ({
      ...this.state.configurationsByTermbaseIds[ticket].lastSelectedColumnConfig,
      locale: {
        ...this.state.configurationsByTermbaseIds[ticket].lastSelectedColumnConfig.locale,
        [locale]: predefinedAttributeType
      }
    }) : ({
      locale: Object.keys(this.state.configurationsByTermbaseIds[ticket].lastSelectedColumnConfig.locale).reduce((result, localeKey) => ({
        ...result,
        [localeKey]: predefinedAttributeType
      }), {}),
      global: predefinedAttributeType
    });
    this.setState({
      configurationsByTermbaseIds: {
        ...this.state.configurationsByTermbaseIds,
        [ticket]: {
          ...this.state.configurationsByTermbaseIds[ticket],
          lastSelectedColumnConfig
        }
      }
    });
  }

  /**
   * Renderer for attributes controlles
   * @param ticket
   * @param locale
   * @param isGlobalControl
   */
  getAttributesControl = (ticket, locale?, isGlobalControl = false) => {
    const mandatoryAttributes = this.getTermbaseMandatoryAttributes(ticket);
    const lastSelectedColumnConfig = this.state.configurationsByTermbaseIds[ticket].lastSelectedColumnConfig;
    const lastSelectedAttr = locale ? lastSelectedColumnConfig.locale[locale] : lastSelectedColumnConfig.global;
    const themeColor = isGlobalControl ? 'primary' : 'dark';
    const columnVisibilityConfig = this.getColumnVisibilityConfig(ticket);
    return (
      <HBox
        className={'tp-attributes-control'}
        gap={1}
      >
        <PuzzleButton
          iconClass={'tp-icon tp-term-only'}
          withTooltip
          themeColor={themeColor}
          size={'small'}
          fillMode={PREDEFINED_ATTRIBUTE_OPTIONS.terms === lastSelectedAttr ? undefined : 'flat'}
          titleText={this.translate('attributesControl.termsOnly')}
          onClick={() => {
            this.onColumnReconfigureVisibility(columnVisibilityConfig.termsOnly, ticket, locale);
            this.setGridColumnConfig(ticket, PREDEFINED_ATTRIBUTE_OPTIONS.terms, locale);
          }}
        />
        <PuzzleButton
          iconClass={'tp-icon tp-mandatory-attributes'}
          withTooltip
          themeColor={themeColor}
          size={'small'}
          fillMode={PREDEFINED_ATTRIBUTE_OPTIONS.mandatory === lastSelectedAttr ? undefined : 'flat'}
          titleText={this.translate('attributesControl.mandatory')}
          onClick={() => {
            this.onColumnReconfigureVisibility({
              ...columnVisibilityConfig.termsOnly,
              ...mandatoryAttributes.reduce((acc, attribute) => ({
                ...acc,
                [attribute]: true
              }), {})
            }, ticket, locale);
            this.setGridColumnConfig(ticket, PREDEFINED_ATTRIBUTE_OPTIONS.mandatory, locale);
          }}
        />
        <PuzzleButton
          iconClass={'tp-icon tp-all-attributes'}
          withTooltip
          themeColor={themeColor}
          size={'small'}
          fillMode={PREDEFINED_ATTRIBUTE_OPTIONS.all === lastSelectedAttr ? undefined : 'flat'}
          titleText={this.translate('attributesControl.all')}
          onClick={() => {
            this.onColumnReconfigureVisibility(columnVisibilityConfig.all, ticket, locale);
            this.setGridColumnConfig(ticket, PREDEFINED_ATTRIBUTE_OPTIONS.all, locale);
          }}
        />
      </HBox>
    );
  }

  /**
   * Handler for columns visibility reconfiguration
   * @param config
   * @param ticket
   * @param locale
   */
  onColumnReconfigureVisibility = (config, ticket, locale?) => {
    let configurationsByTermbaseIds = {
      ...this.state.configurationsByTermbaseIds
    };
    if (ticket && locale) {
      configurationsByTermbaseIds[ticket].columnVisibilityByLanguages[locale] = config;
    } else {
      const termAttributeNames = this.getTermAttributes(ticket).map(attr => attr.name);
      const termEntryAttributeNames = this.getTermEntryAttributes(ticket).map(attr => attr.name);
      const termEntryColumnsConfig = {};
      const termColumnsConfig = {};
      Object.entries(config).forEach(([key, value]) => {
        if (termAttributeNames.includes(key) || TERM_PREDEFINED_COLUMNS.includes(key)) {
          termColumnsConfig[key] = value;
        }
        if (termEntryAttributeNames.includes(key) || TERM_ENTRY_PREDEFINED_COLUMNS.includes(key)) {
          termEntryColumnsConfig[key] = value;
        }
      });
      Object.keys(configurationsByTermbaseIds).forEach((tempTermId) => {
        configurationsByTermbaseIds[tempTermId].columnVisibilityByEntry = termEntryColumnsConfig;
        Object.keys(configurationsByTermbaseIds[tempTermId].columnVisibilityByLanguages).forEach((languageKey) => {
          configurationsByTermbaseIds[tempTermId].columnVisibilityByLanguages[languageKey] = termColumnsConfig;
        });
      });
    }
    this.setState({
      configurationsByTermbaseIds
    }, () => this.reconfigureColumns(ticket));
  }

  /**
   * Reconfigure columns handler
   * @param ticket
   */
  reconfigureColumns = (ticket) => {
    this.dispatch('ReconfigureColumns', Position.end, {
      columns: this.getTermColumns(ticket)
    });
  }

  /**
   * Listener that triggers if row is deselected
   */
  private onRowDeselected = () => {
    setTimeout(() => {
      this.setState({
        isRowSelected: false
      });
    });
  };
  /**
   * Update the state based on the selected row's data.
   * @param termbase - The data of the selected row.
   */
  private onRowSelected = (termbase: TERMBASE | TERM_ENTRY_ITEM) => {
    const isRowSelected = !_.isEmpty(termbase);
    setTimeout(() => {
      this.setState({
        dataItem: termbase,
        isRowSelected
      });
    });
  };

  /**
   * Getter for termbaseAtrributes for termbase ticket id
   * @param ticket
   */
  private getTermbaseAttributes = (ticket: string) => this.state.configurationsByTermbaseIds[ticket].termbaseAttributes;

  /**
   * Getter for termbaseAtrribute names for termbase ticket id
   * @param ticket
   */
  private getTermbaseAttributeNames = (ticket: string) => this.getTermbaseAttributes(ticket)
    .map(attribute => attribute.name)

  /**
   * Getter for mandatory termbaseAtrributes for termbase ticket id
   * @param ticket
   */
  private getTermbaseMandatoryAttributes = (ticket: string) => this.getTermbaseAttributes(ticket)
    .filter(attribute => attribute.mandatory)
    .map(attribute => attribute.name);

  private getColumnVisibilityConfig = (ticket: string) => {
    const allAttributes = this.getTermbaseAttributeNames(ticket);
    return ({
      termsOnly: {
        ...allAttributes.reduce((acc, value) => ({
          ...acc,
          [value]: false
        }), {}),
        'Term status': true
      },
      all: allAttributes.reduce((acc, value) => ({
        ...acc,
        [value]: true
      }), {})
    });
  }

  /**
   * Termbase double click handler, opens that termbase
   * @param dataItem
   */
  private onRowDoubleClick = ({ dataItem } : { dataItem: TERMBASE }) => {
    const { languages, ticket, anchorLanguage, termbaseAttributes = [] } = dataItem;

    const { configurationsByTermbaseIds } = this.state;
    let newConfigurationsByTermbaseIds: CONFIGURATION_BY_TERMBASE_IDS = {
      ...configurationsByTermbaseIds,
      [ticket]: {
        termbaseAttributes,
        languagesMap: {
          languages,
          anchorLanguage
        },
        visibleLanguages: languages.map(lang => lang),
        columnVisibilityByEntry: {},
        columnVisibilityByLanguages: languages.reduce((previousValue, currentValue) => ({
          ...previousValue,
          [currentValue.locale]: termbaseAttributes.reduce((acc, attribute) => ({
            ...acc,
            [attribute.name]: PRESELECTED_ATTRIBUTES.includes(attribute.name) ? true : false
          }), {})
        }), {}),
        lastSelectedColumnConfig: {
          locale: languages.reduce((acc, language) => ({
            ...acc,
            [language.locale]: PREDEFINED_ATTRIBUTE_OPTIONS.terms
          }), {}),
          global: PREDEFINED_ATTRIBUTE_OPTIONS.terms
        }
      }
    };

    this.setState({
      configurationsByTermbaseIds: newConfigurationsByTermbaseIds
    }, () => {
      this.dispatch('addTabItem', Position.end, {
        tabItemConfig: this.getTermsGridConfig(dataItem)
      });
      this.onRowDeselected();
    });
  }

  /**
   * Term item parser function
   * @param term
   * @param ticket
   * @param languagesLocale
   */
  private parseTermItem = (term, ticket) => {
    const tempItem = {
      id: term.id
    };
    this.getTermbaseAttributeNames(ticket).forEach((attribute) => {
      tempItem[`${term.languageId}-${attribute}`] = [];
    });
    Object.keys(term).forEach((singleTermKey) => {
      if (!['id'].includes(singleTermKey)) {
        if (singleTermKey === 'termDescriptions') {
          term.termDescriptions.forEach((description) => {
            tempItem[`${term.languageId}-${description.name}`] = description.value;
          });
        } else {
          tempItem[`${term.languageId}-${singleTermKey}`] = term[singleTermKey];
        }
      }
    });
    return tempItem;
  }

  /**
   * Getter for terms grid configuration
   * @param ticket
   * @param name
   * @param anchorLanguage
   * @param languages
   */
  private getTermsGridConfig = ({ ticket, name, anchorLanguage, languages }: TERMBASE) => ({
    config: {
      rowSkeletonEnabled: true,
      autoHeightRowCorrection: true,
      keyboardShortcuts: true,
      id: 'termsGrid',
      selectableAll: 'checkboxAll',
      gridType: 'tree' as const,
      onRowSelected: this.onRowSelected,
      onRowDeselected: this.onRowDeselected,
      uniqueFieldDetail: 'id',
      requestConfig: {
        method: 'post',
        url: '/terminology',
        params: {
          sourceLanguage: anchorLanguage,
          targetLanguages: languages.map(lang => lang.locale),
          termbaseFilter: {
            termbaseTickets: [ticket]
          }
        }
      },
      hasExpandDetailButton: (dataItem, columnDataIndex) => {
        const { subItems = [] } = dataItem;
        const locale = columnDataIndex.replace('-termValue-expanded', '');
        let foundLocale = '';
        subItems.every((subItem) => {
          if (_.isArray(subItem.languagesPopulated)) {
            foundLocale = subItem.languagesPopulated.find(item => locale === item);
            return !foundLocale;
          }
          return true;
        });
        return foundLocale;
      },
      parseGridData: (data) => {
        const items = [];
        const languagesLocale = languages.map(lang => lang.locale);
        const ticket = this.state.activeTabUniqueName;
        data.content.forEach((termItem: TERM_ENTRY_ITEM) => {
          let tempItem = {
            termEntryId: termItem.termEntryId,
            subItems: []
          };
          TERM_ENTRY_PREDEFINED_COLUMNS.forEach((columnName) => {
            tempItem[columnName] = termItem[columnName];
          });
          termItem.termEntryDescriptions.forEach((desc) => {
            tempItem[desc.name] = desc.value;
          });
          termItem.terms.forEach((singleTerm) => {
            if (singleTerm.mainTerm) {
              tempItem = {
                ...tempItem,
                ...this.parseTermItem(singleTerm, ticket)
              };
            } else {
              const parsedSubTerm = this.parseTermItem(singleTerm, ticket);
              const notPopulatedIndex = tempItem.subItems
                .findIndex(subItem => !subItem.languagesPopulated.includes(singleTerm.languageId));
              if (notPopulatedIndex === -1) {
                tempItem.subItems.push({
                  languagesPopulated: [singleTerm.languageId],
                  ...parsedSubTerm
                });
              } else {
                tempItem.subItems[notPopulatedIndex] = {
                  ...tempItem.subItems[notPopulatedIndex],
                  ...parsedSubTerm,
                  languagesPopulated: tempItem.subItems[notPopulatedIndex].languagesPopulated
                    .concat([singleTerm.languageId])
                };
              }
            }
          });
          items.push(tempItem);
        });

        return ({
          items,
          gridContentInfo: {
            totalCount: data.totalElements,
            pagedListInfo: {
              index: data.number,
              size: data.size
            }
          }
        });
      },
      className: 'terms-grid',
      columns: this.getTermColumns(ticket),
      isPreloadedSubitems: true,
      reorderable: false,
      uniqueField: 'termEntryId',
      pageSize: 40,
      rowHeight: 41,
      height: 400
    },
    uniqueItemValue: ticket,
    title: name
  })

  /**
   * Getter for termbases grid configuration
   */
  getTermbasesColumns = (): DASHBOARD_COLUMN_CONFIG[] => {
    const { hideColumnsForNonAdmins } = this.props;

    return [{
      dataIndex: 'name',
      header: this.translate('columns.name'),
      width: 200,
      columnMenuConfig: {
        requestConfig: {
          url: '/termbases',
          method: 'get',
          params: {
            field: 'name'
          }
        },
        type: 'virtual-dom-list-search'
      }
    }, {
      dataIndex: 'shortCode',
      cellType: 'input',
      header: this.translate('columns.shortCode'),
      width: 200,
      columnMenuConfig: {
        type: 'input'
      }
    }, {
      dataIndex: 'active',
      cellConfig: {
        align: 'center',
        icons: ['tp-close tp-color-red-400', 'tp-check tp-color-grey-600']
      },
      cellType: 'icon',
      header: this.translate('columns.active'),
      width: 200,
      columnMenuConfig: {
        data: [{
          _index: 0,
          value: 'true',
          name: this.translate('columns.active'),
          _populated: true
        }, {
          _index: 1,
          value: 'false',
          name: this.translate('columns.inactive'),
          _populated: true
        }],
        singleSelectList: true,
        type: 'list'
      }
    }, {
      dataIndex: 'organizationName',
      header: this.translate('columns.organization'),
      width: 350,
      columnMenuConfig: {
        type: 'input'
      }
    }, {
      dataIndex: 'numberOfLanguages',
      header: this.translate('columns.languages'),
      width: 200,
      columnMenuConfig: {
        filterable: false
      },
      hidden: hideColumnsForNonAdmins
    }, {
      dataIndex: 'numberOfUserGroups',
      header: this.translate('columns.userGroups'),
      cellType: 'routeLink',
      columnMenuConfig: {
        filterable: false
      },
      cellConfig: {
        actionData: {
          path: '/view/user-groups',
          dashboardModuleName: 'UserGroupsDashboard',
          filterDataMap: [{
            filterFieldName: 'termbaseTicket',
            valueFieldName: 'ticket'
          }]
        }
      },
      format: 'number',
      width: 200,
      hidden: hideColumnsForNonAdmins
    }, {
      dataIndex: 'numberOfUsers',
      header: this.translate('columns.user'),
      cellType: 'routeLink',
      format: 'number',
      width: 200,
      columnMenuConfig: {
        filterable: false
      },
      cellConfig: {
        actionData: {
          path: '/view/users',
          dashboardModuleName: 'UsersDashboard',
          filterDataMap: [
            {
              valueFieldName: 'ticket',
              filterFieldName: 'termbaseTicket'
            }
          ]
        }
      },
      hidden: hideColumnsForNonAdmins
    }, {
      dataIndex: 'createdOn',
      header: this.translate('columns.createdOn'),
      width: 200,
      cellConfig: {
        format: 'MMM. D, YYYY HH:mm:ss'
      },
      cellType: 'date',
      columnMenuConfig: {
        filterable: false
      }
    }, {
      dataIndex: 'createdBy',
      header: this.translate('columns.createdBy'),
      width: 200,
      columnMenuConfig: {
        filterable: false
      }
    }, {
      dataIndex: 'modifiedOn',
      header: this.translate('columns.modifiedOn'),
      width: 200,
      cellConfig: {
        format: 'MMM. D, YYYY HH:mm:ss'
      },
      cellType: 'date',
      columnMenuConfig: {
        filterable: false
      }
    }, {
      dataIndex: 'modifiedBy',
      header: this.translate('columns.modifiedBy'),
      width: 200,
      columnMenuConfig: {
        filterable: false
      }
    }];
  }

  /**
   * Getter for termbases home grid configuration
   */
  getTermbasesHomeGridConfig = (): DASHBOARD_GRID_CONFIG => ({
    closable: false,
    columns: this.getTermbasesColumns(),
    filterData: this.props.filterData,
    onRowDeselected: this.onRowDeselected,
    onRowDoubleClick: this.onRowDoubleClick,
    onRowSelected: this.onRowSelected,
    requestConfig: {
      method: 'get',
      url: '/termbases'
    },
    uniqueField: 'ticket'
  });

  /**
   * Getter for information is terms grid opened
   */
  isTermsGridTabOpened = () => this.state.activeTabUniqueName && this.state.activeTabUniqueName !== 'home'

  /**
   * Getter for Termbase toolbar actions
   */
  getTermbaseActions = (dataItem: TERMBASE, isRowSelected) => [{
    text: this.translate('createTermbaseActionText'),
    fillMode: 'solid' as const,
    iconClass: 'tp-icon tp-add',
    themeColor: 'success' as const,
    hidden: this.props.hideColumnsForNonAdmins,
    action: {
      actionName: 'openCreateTermbase'
    }
  }, {
    text: this.translate('editTermbaseActionText'),
    fillMode: 'solid' as const,
    iconClass: 'tp-icon tp-edit1',
    disabled: !isRowSelected,
    hidden: this.props.hideColumnsForNonAdmins,
    action: {
      actionName: 'openEditTermbase',
      data: { ticket: dataItem.ticket }
    }
  }, {
    text: this.translate('activeDeactivateToggleTermbaseActionText'),
    fillMode: 'solid' as const,
    iconClass: 'tp-icon tp-activate1',
    disabled: !isRowSelected,
    hidden: this.props.hideColumnsForNonAdmins,
    action: {
      actionName: 'activeDeactivateToggleTermbase',
      data: { data: dataItem }
    }
  }, {
    text: this.translate('importTermbaseAction'),
    fillMode: 'solid' as const,
    iconClass: 'tp-icon tp-download',
    disabled: !isRowSelected,
    hidden: this.props.hideColumnsForNonAdmins,
    action: {
      actionName: 'importTermbase',
      data: {
        path: `/import-termbase/${dataItem.ticket}`,
        ticket: dataItem.ticket
      }
    }
  }, {
    text: this.translate('exportTermbaseAction'),
    fillMode: 'solid' as const,
    iconClass: 'tp-icon tp-export',
    action: {
      actionName: 'exportTermbaseViewAction',
      data: {
        ticket: dataItem.ticket
      }
    },
    disabled: !isRowSelected
  }, {
    text: this.translate('deleteActionText'),
    fillMode: 'solid' as const,
    iconClass: 'tp-icon tp-delete1',
    disabled: !isRowSelected,
    hidden: this.props.hideColumnsForNonAdmins,
    action: {
      actionName: 'deleteTermbase'
    }
  }];

  /**
   * Getter for Terms toolbar actions
   */
  getTermsActions = (dataItem: TERM_ENTRY_ITEM, isRowSelected) => {
    const ticket = this.state.activeTabUniqueName;
    return [{
      text: this.translate('importTermAction'),
      fillMode: 'solid' as const,
      iconClass: 'tp-icon tp-download',
      action: {
        actionName: 'importTermbase',
        data: {
          path: `/import-termbase/${ticket}`,
          ticket
        }
      },
      disabled: false
    }, {
      text: this.translate('exportTermAction'),
      fillMode: 'solid' as const,
      iconClass: 'tp-icon tp-export',
      action: {
        actionName: 'exportTermbaseViewAction',
        data: {
          ticket
        }
      }
    }];
  }

  /**
   * Getter for toolbar actions
   */
  getToolbarActions = () => {
    const { isRowSelected, dataItem } = this.state;
    return {
      actionsBeforeDropDown: 9,
      actions: this.isTermsGridTabOpened()
        ? this.getTermsActions(dataItem as TERM_ENTRY_ITEM, isRowSelected)
        : this.getTermbaseActions(dataItem as TERMBASE, isRowSelected)
    };
  }

  /**
   * Getter for grid additional buttons configuration
   */
  getAdditionalButtons = () =>
    this.isTermsGridTabOpened()
      ? this.getDashboardTermActions(this.state.activeTabUniqueName)
      : []
  
  /**
   * Getter for header title
   */
  getHeaderTitle = () => this.translate(`title.${this.isTermsGridTabOpened() ? 'terms' : 'termbase'}`)

  /**
   * Getter for search placeholder text
   */
  getSearchTermbasePlaceholderText = () => this.translate(`placeholders.${this.isTermsGridTabOpened() ? 'terms' : 'termbase'}`)

  /**
   * On language visibility and ordering change handler
   * @param event
   */
  onLanguageSelectorChange = (event) => {
    const activeTabTicket = this.state.activeTabUniqueName;
    const visibleLanguages = event.selectedItems.map(item => ({
      value: item.text,
      locale: item.value
    }));
    let selectedLastLanguage;
    if (visibleLanguages.length === 0) {
      const { anchorLanguage, languages } = this.state.configurationsByTermbaseIds[activeTabTicket].languagesMap;
      selectedLastLanguage = languages.find(lang => lang.locale === anchorLanguage);
      visibleLanguages.push({
        ...selectedLastLanguage
      });
    }
    this.setState({
      configurationsByTermbaseIds: {
        ...this.state.configurationsByTermbaseIds,
        [activeTabTicket]: {
          ...this.state.configurationsByTermbaseIds[activeTabTicket],
          visibleLanguages
        }
      }
    }, () => this.reconfigureColumns(activeTabTicket));
    if (selectedLastLanguage) {
      return [{
        value: selectedLastLanguage.locale,
        selected: true
      }];
    }
  };

  /**
   * Renderer for custom languages column button
   */
  getLanguagesColumnButton = () => {
    const activeTabTicket = this.state.activeTabUniqueName;
    const { visibleLanguages, languagesMap } = this.state.configurationsByTermbaseIds[activeTabTicket];
    const allLanguages = this.getSelectedTermLanguages(activeTabTicket);
    return (
      <PuzzleCheckboxMultiselect
        title={this.translate('columnVisibilityButton.title')}
        disabledItems={visibleLanguages.length === 1 ? [visibleLanguages[0].locale] : []}
        data={allLanguages.map((language) => {
          const selected = visibleLanguages.map(lang => lang.locale).includes(language.locale);
          return ({
            value: language.locale,
            text: language.value,
            icon: languagesMap.anchorLanguage === language.locale ? 'tp-anchor' : undefined,
            selected
          });
        })}
        configListItemAllCheck={{
          text: this.translate('columnVisibilityButton.allLanguages'),
          selected: visibleLanguages.length === allLanguages.length
        }}
        onReorder={this.onLanguageSelectorChange}
        onChange={this.onLanguageSelectorChange}
      />
    );
  }

  /**
   * Component renderer
   */
  renderBody(): any {
    const isTerminologyGridOpened = this.isTermsGridTabOpened();
    return (
      <Dashboard
        className="tp-termbases-dashboard"
        searchBy={'name'}
        placeholder={this.getSearchTermbasePlaceholderText()}
        moduleId={'TermbasesDashboard'}
        title={this.getHeaderTitle()}
        subtitle={{ items: 'entries' }}
        drawerConfig={{}}
        onSelectActiveTab={this.onSelectActiveTab}
        searchConfig={{
          endPoint: '/termbases',
          searchKey: isTerminologyGridOpened ? 'textFilter.searchValue' : 'search',
          placeholder: this.getSearchTermbasePlaceholderText(),
          dashboard: 'TERMBASES',
          filterConfig: {
            endPoints: {},
            fields: []
          }
        }}
        showAdvancedFilters={true}
        additionalBtns={this.getAdditionalButtons()}
        columnsButtonCustomRenderer={this.isTermsGridTabOpened() ? this.getLanguagesColumnButton : undefined}
        toolbarActions={this.getToolbarActions()}
        tabPanelConfig={{
          tabStripProps: {
            keepTabsMounted: false
          },
          selected: 0,
          items: [{
            config: this.getTermbasesHomeGridConfig(),
            renderType: DashboardGrid,
            title: 'Home'
          }]
        }}
      />
    );
  }
}

export default TermDashboard;
